import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { EnterPage } from './enter-page';
import AppShell from './layouts/AppShell';
import LoaderWithTimeout from './components/LoaderWithTimeout';
import MaintenanceScreen from './components/MaintenanceScreen';
import { NavigationProvider } from './hooks/useNavigationHistory';
import { usePlatformConfig } from './hooks';
import retryLazyLoad from './utils/lazyWithRetry';
import ErrorBoundary from './shared/components/ErrorBoundary';

// Lazy load pages for code splitting with retry logic
const LaunchPage = retryLazyLoad(() => import('./pages/LaunchPage'));
const TrendsPage = retryLazyLoad(() => import('./pages/TrendsPage'));
const PortfolioPage = retryLazyLoad(() => import('./pages/PortfolioPage'));
const ToolsPage = retryLazyLoad(() => import('./pages/ToolsPage'));
const SettingsPage = retryLazyLoad(() => import('./pages/SettingsPage'));
const BookletPage = retryLazyLoad(() => import('./booklet/BookletPage'));
const GraduatedPage = retryLazyLoad(() => import('./pages/GraduatedPage'));
const WalletConnectPage = retryLazyLoad(() => import('./pages/WalletConnectPage'));
const LabPage = retryLazyLoad(() => import('./pages/LabPage'));
const DocsPage = retryLazyLoad(() => import('./pages/DocsPage'));
const HomePage = retryLazyLoad(() => import('./pages/HomePage'));
const TermsPage = retryLazyLoad(() => import('./pages/TermsPage'));
const ApiStatusPage = retryLazyLoad(() => import('./pages/ApiStatusPage'));

const isDevelopment = process.env.NODE_ENV === 'development';
const isPreview = process.env.REACT_APP_PREVIEW === 'true';
// Show landing page ONLY on yobitch.fun domain - everywhere else show app directly
const isProductionDomain = typeof window !== 'undefined' && window.location.hostname === 'yobitch.fun';
// Force app at root for: localhost, 127.0.0.1, any IP, vercel preview, or non-production domain
const showAppAtRoot = !isProductionDomain; // Simple: if not yobitch.fun, show app

// Development/Local routes - App at root, Enter page at /landing for testing
const DevRoutes = () => (
  <Routes>
    <Route path="/landing" element={<EnterPage />} />
    <Route path="/connect" element={
      <ErrorBoundary>
        <Suspense fallback={<LoaderWithTimeout />}>
          <WalletConnectPage />
        </Suspense>
      </ErrorBoundary>
    } />
    <Route path="/docs" element={
      <ErrorBoundary>
        <Suspense fallback={<LoaderWithTimeout />}>
          <DocsPage />
        </Suspense>
      </ErrorBoundary>
    } />
    <Route path="/about" element={
      <ErrorBoundary>
        <Suspense fallback={<LoaderWithTimeout />}>
          <HomePage />
        </Suspense>
      </ErrorBoundary>
    } />
    <Route path="/terms" element={
      <ErrorBoundary>
        <Suspense fallback={<LoaderWithTimeout />}>
          <TermsPage />
        </Suspense>
      </ErrorBoundary>
    } />
    <Route path="/" element={<AppShell />}>
      <Route index element={<Navigate to="/launch" replace />} />
      <Route path="launch" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <LaunchPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="trends" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <TrendsPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="portfolio" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <PortfolioPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="tools" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <ToolsPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="tools/:toolId" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <ToolsPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="settings" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <SettingsPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="booklet" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <BookletPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="graduated" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <GraduatedPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="lab" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <LabPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="status" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <ApiStatusPage />
          </Suspense>
        </ErrorBoundary>
      } />
    </Route>
    <Route path="/app/*" element={<Navigate to="/" replace />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

// Production routes - Enter page at root, app at /app
const ProdRoutes = () => (
  <Routes>
    <Route path="/" element={<EnterPage />} />
    <Route path="/app/connect" element={
      <ErrorBoundary>
        <Suspense fallback={<LoaderWithTimeout />}>
          <WalletConnectPage />
        </Suspense>
      </ErrorBoundary>
    } />
    <Route path="/app/docs" element={
      <ErrorBoundary>
        <Suspense fallback={<LoaderWithTimeout />}>
          <DocsPage />
        </Suspense>
      </ErrorBoundary>
    } />
    <Route path="/app/about" element={
      <ErrorBoundary>
        <Suspense fallback={<LoaderWithTimeout />}>
          <HomePage />
        </Suspense>
      </ErrorBoundary>
    } />
    <Route path="/app/terms" element={
      <ErrorBoundary>
        <Suspense fallback={<LoaderWithTimeout />}>
          <TermsPage />
        </Suspense>
      </ErrorBoundary>
    } />
    <Route path="/app" element={<AppShell />}>
      <Route index element={<Navigate to="/app/launch" replace />} />
      <Route path="launch" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <LaunchPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="trends" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <TrendsPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="portfolio" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <PortfolioPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="tools" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <ToolsPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="tools/:toolId" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <ToolsPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="settings" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <SettingsPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="booklet" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <BookletPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="graduated" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <GraduatedPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="lab" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <LabPage />
          </Suspense>
        </ErrorBoundary>
      } />
      <Route path="status" element={
        <ErrorBoundary>
          <Suspense fallback={<LoaderWithTimeout />}>
            <ApiStatusPage />
          </Suspense>
        </ErrorBoundary>
      } />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

// Wrapper component to check maintenance mode
const AppContent = () => {
  const { maintenance, config, loading } = usePlatformConfig();
  
  // Don't block while loading - show app
  if (loading) {
    return showAppAtRoot ? <DevRoutes /> : <ProdRoutes />;
  }
  
  // Show maintenance screen if enabled (but allow settings page for admin access)
  if (maintenance && window.location.pathname !== '/app/settings' && window.location.pathname !== '/settings') {
    return <MaintenanceScreen message={config?.maintenance?.message} />;
  }
  
  return showAppAtRoot ? <DevRoutes /> : <ProdRoutes />;
};

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <NavigationProvider>
        <AppContent />
      </NavigationProvider>
    </BrowserRouter>
  );
}
